import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Filter chip color configuration
 * Modify these values to change filter chip colors across the app
 */
const FILTER_COLORS = {
  draft: {
    filled: 'bg-[var(--pm-bg-card-alt)] text-[var(--pm-text-primary)] border-[var(--pm-border-default)]',
    outline: 'text-[var(--pm-text-faint)] border-[var(--pm-border-default)] hover:bg-[var(--pm-surface-3)]',
  },
  needsReview: {
    filled: 'bg-amber-600/60 text-white border-amber-600',
    outline: 'text-amber-500 border-amber-500 hover:bg-amber-500/10',
  },
  ready: {
    filled: 'bg-teal-600/60  text-white border-teal-600',
    outline: 'text-teal-500 border-teal-500 hover:bg-teal-500/10',
  },
  published: {
    filled: 'bg-green-600/60 text-white border-green-600',
    outline: 'text-green-500 border-green-500 hover:bg-green-500/10',
  },
  failed: {
    filled: 'bg-red-600/60  text-white border-red-600',
    outline: 'text-red-500 border-red-500 hover:bg-red-500/10',
  },
  archived: {
    filled: 'bg-[var(--pm-bg-card-alt)] text-[var(--pm-text-primary)] border-[var(--pm-border-default)]',
    outline: 'text-[var(--pm-text-faint)] border-[var(--pm-border-default)] hover:bg-[var(--pm-surface-3)]',
  },
} as const;

export type FilterCategory = keyof typeof FILTER_COLORS;

/**
 * Filter chip display labels
 */
const FILTER_LABELS: Record<FilterCategory, string> = {
  draft: 'Draft',
  needsReview: 'Needs Review',
  ready: 'Ready',
  published: 'Published',
  failed: 'Failed',
  archived: 'Archived',
};

/**
 * Default order of filter chips
 */
const FILTER_ORDER: FilterCategory[] = [
  'draft',
  'needsReview',
  'ready',
  'published',
  'failed',
  'archived',
];

interface FilterChipsProps {
  selectedFilters: Set<FilterCategory>;
  onToggle: (category: FilterCategory) => void;
  /** Optional subset of filters to display (defaults to all in order) */
  filters?: FilterCategory[];
  className?: string;
}

/**
 * Reusable filter chips component for filtering episodes by status category.
 * Colors are configured at the top of this file for easy modification.
 */
export const FilterChips: React.FC<FilterChipsProps> = ({
  selectedFilters,
  onToggle,
  filters = FILTER_ORDER,
  className,
}) => {
  return (
    <div className={cn('flex gap-1.5', className)}>
      {filters.map((category) => {
        const isSelected = selectedFilters.has(category);
        const colors = FILTER_COLORS[category];
        const label = FILTER_LABELS[category];

        return (
          <button
            key={category}
            onClick={() => onToggle(category)}
            className={cn(
              'px-2 py-0.5 text-xs font-medium rounded-full border transition-colors bg-transparent',
              isSelected ? colors.filled : colors.outline
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
};

export default FilterChips;
